import Cookies from 'cookies-js'
import md5 from 'blueimp-md5'
import uuid from 'uuid'

import sharedCookieDomain from './lib/sharedCookieDomain'

export const COOKIE_NAME = 'ft'
export const OVERRIDE_COOKIE_NAME = 'ft-exp'
const LEAST_SIGNIFICANT_BITS = -13
const HASH_MOD_VALUE = 100

export function calcBucketId(anonId, salt) {
    const key = `${anonId}-${salt}`
    // MD5 returns 128-bits encoded in hex. Javascript's max safe integer is 2^53 - 1,
    // which can only be represented in 13 hex chars (16^13 = 2^52). Since md5 is
    // randomly distributed (that's why we're using it, after all), we can take any 13
    // chars in the hash. So we arbitrarily decided to take the 13 least significant bits.
    const hash = md5(key).slice(LEAST_SIGNIFICANT_BITS)
    // Convert hex to int and then mod into buckets from 0 to 99 (inclusive)

    return parseInt(hash, 16) % HASH_MOD_VALUE
}

export function getCurrentExperimentAndVariantNames({
    experiments,
    salt,
    anonId,
}) {
    const bucketId = calcBucketId(anonId, salt)

    for (let i = 0; i < experiments.length; i++) {
        const { experiment, variant, range } = experiments[i]

        if (range[0] <= bucketId && bucketId <= range[1]) {
            return {
                experiment,
                variant,
            }
        }
    }
    return {
        experiment: 'default',
        variant: 'control',
    }
}

function buildCookieString(userId, anonId) {
    return [userId || '', anonId].join('|')
}

const SALT = '23467' // Use salt to "shake up" the distribution of users within experiment buckets

export const EXPERIMENTS = [
    {
        experiment: 'default',
        variant: 'active',
        range: [0, 49], // eslint-disable-line
    },
]

export class Context {
    constructor({
        expConfig = EXPERIMENTS,
        expSalt = SALT,
        win = window,
    } = {}) {
        const [prevUserId, prevAnonId] = (Cookies.get(COOKIE_NAME) || '').split(
            '|',
        )
        const [overrideExperiment, overrideVariant] = (
            Cookies.get(OVERRIDE_COOKIE_NAME) || ''
        ).split('|')

        this._userId = prevUserId || null
        this._anonId = prevAnonId || uuid.v4()

        if (overrideExperiment && overrideVariant) {
            this._experiment = overrideExperiment
            this._variant = overrideVariant
        } else {
            const { experiment, variant } = getCurrentExperimentAndVariantNames(
                {
                    experiments: expConfig,
                    salt: expSalt,
                    anonId: this._anonId,
                },
            )

            this._experiment = experiment
            this._variant = variant
        }

        this._win = win

        this.persist()
    }

    persist() {
        // Set user ID and anon ID for as long as possible
        const newCookie = buildCookieString(this._userId, this._anonId)

        Cookies.set(COOKIE_NAME, newCookie, {
            expires: Infinity,
            domain: sharedCookieDomain(this._win.location),
        })
    }

    // User ID probably shouldn't be in this module since it cannot
    // be determined at boot time (at least in the client) and so should
    // not be used to determine experiment eligibility.
    setUserId(userId) {
        this._userId = userId ? String(userId) : null
        this.persist()
    }

    setTrueUserId(userId) {
        this._trueUserId = userId ? String(userId) : null
    }

    setProductVersion(version) {
        this._version = version
    }

    setProductName(product) {
        this._product = product
    }

    getExperiment() {
        return this._experiment
    }

    getVariant() {
        return this._variant
    }

    toJSON() {
        return {
            product: this._product,
            version: this._version,
            user_id: this._userId, // eslint-disable-line camelcase
            true_user_id: this._trueUserId, // eslint-disable-line camelcase
            anon_id: this._anonId, // eslint-disable-line camelcase
            experiment: this._experiment,
            variant: this._variant,
            referrer: String(this._win.location),
        }
    }

    isVariantActive({ experiment, variant }) {
        return this._experiment === experiment && this._variant === variant
    }
}

export default new Context()
